import { useFlowSessionById } from "@/api/flow_sessions";
import { PERMISSION_MODES, PermissionMode } from "@cohesive/api-client";
import { useEffect, useSyncExternalStore } from "react";

// Pending picks keyed by session, module-level so every control instance on a
// surface agrees: the composer, its empty-state suggested actions, and retry
// all send the same session's next message, and each stamps the same pick.
const pendingPicks = new Map<string, PermissionMode>();
const pickListeners = new Set<() => void>();

function setPendingPick(
  flowSessionId: string,
  mode: PermissionMode | undefined,
) {
  if (mode === undefined) {
    pendingPicks.delete(flowSessionId);
  } else {
    pendingPicks.set(flowSessionId, mode);
  }
  pickListeners.forEach((listener) => listener());
}

function subscribeToPicks(listener: () => void) {
  pickListeners.add(listener);
  return () => pickListeners.delete(listener);
}

/** Test seam: the pick store outlives component unmounts. */
export function clearPendingPermissionModePicks() {
  pendingPicks.clear();
  pickListeners.forEach((listener) => listener());
}

export interface UsePermissionModeControlParams {
  /** The session this composer posts to, if any. Its row holds the current mode. */
  flowSessionId?: string;
  /** Draft choice, used only while there is no session. */
  draftMode?: PermissionMode;
  /** Set the draft. Its presence is what makes a session-less control interactive. */
  onDraftModeChange?: (mode: PermissionMode) => void;
}

export interface PermissionModeControl {
  /** True while the control drives a session-less draft. */
  isDraft: boolean;
  /**
   * The mode the control shows — the pending pick, else the session's, else the draft — or
   * undefined when there is nothing to assert: no draft handler and no session object in hand
   * (still loading, or the fetch failed).
   */
  mode: PermissionMode | undefined;
  setMode: (mode: PermissionMode) => void;
  /**
   * The mode to stamp on the next message, or undefined to leave the session's alone. Set only
   * where the pick differs from what the session already has: the switch is carried by the
   * message, and the server stamps it back onto the row — there is no write here.
   *
   * A draft sends its non-default pick the same way: the first message stamps the freshly
   * minted session, which was created on the default.
   */
  sendPermissionMode: PermissionMode | undefined;
}

/**
 * Resolves the composer's permission mode from the two places it can live: the flow session once
 * one exists, and the caller's draft until then.
 *
 * Mid-chat switches are held here rather than written through, mirroring the model switch next
 * door — the switch is carried by the next message, which also means a flip can never silently
 * answer a permission the session has already asked.
 */
export function usePermissionModeControl({
  flowSessionId,
  draftMode,
  onDraftModeChange,
}: UsePermissionModeControlParams): PermissionModeControl {
  // Shares the query key used elsewhere, so React Query dedupes — no extra fetch.
  const { data: flowSession } = useFlowSessionById(flowSessionId || "");
  const pending = useSyncExternalStore(subscribeToPicks, () =>
    flowSessionId ? pendingPicks.get(flowSessionId) : undefined,
  );

  // A pick the row has caught up with (the send's stamp landing in the cache, or an identical
  // external write) is spent: dropping it keeps it from springing back as a "switch" if the row
  // later changes under it, e.g. re-stamping allow-all onto a session the user just restored
  // through SessionBypassNotice.
  const sessionMode = flowSession?.permission_mode;
  useEffect(() => {
    if (flowSessionId && pending !== undefined && pending === sessionMode) {
      setPendingPick(flowSessionId, undefined);
    }
  }, [flowSessionId, pending, sessionMode]);

  const isDraft = !flowSessionId && !!onDraftModeChange;
  const mode = isDraft
    ? (draftMode ?? PERMISSION_MODES.AUTO)
    : (pending ?? sessionMode);
  // What the row would run without a switch: the session's mode, or the default a freshly
  // minted session gets.
  const baseline = isDraft ? PERMISSION_MODES.AUTO : sessionMode;

  return {
    isDraft,
    mode,
    setMode: (next) => {
      if (isDraft) {
        onDraftModeChange?.(next);
      } else if (flowSessionId) {
        setPendingPick(flowSessionId, next);
      }
    },
    // Measured against the baseline, not against the pick: landing back on what the row already
    // holds is not a switch, however it got there.
    sendPermissionMode:
      mode !== undefined && mode !== baseline ? mode : undefined,
  };
}
